import { useMemo, useState } from "react";
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender,
} from "@tanstack/react-table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCurrency } from "@/context/CurrencyContext";
import { OrderProgressBar } from "@/components/buyer/OrderProgressBar";
import { TrackingTimeline } from "@/components/TrackingTimeline";
import { Package, ArrowUpDown, ChevronLeft, ChevronRight, Eye, RotateCcw, Truck } from "lucide-react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";

const fState = (o) => (o.fulfillment_status || o.status || "processing").toLowerCase();
const statusBadge = (s) => {
  const cls = { pending: "bg-slate-400", processing: "bg-amber-500", packed: "bg-indigo-500", shipped: "bg-blue-500", delivered: "bg-green-600", cancelled: "bg-red-500" }[s] || "bg-amber-500";
  return <Badge className={`${cls} capitalize`}>{s}</Badge>;
};

export const BuyerOrders = ({ orders = [], onRequestReturn, onMessageSeller }) => {
  const { format } = useCurrency();
  const [selected, setSelected] = useState(null);
  const [sorting, setSorting] = useState([{ id: "created_at", desc: true }]);

  const columns = useMemo(() => [
    { accessorKey: "order_id", header: "Order", cell: ({ row }) => <span className="font-mono text-xs font-semibold">#{row.original.order_id.slice(-8)}</span> },
    { accessorKey: "created_at", header: "Date", cell: ({ getValue }) => <span className="text-sm text-muted-foreground">{new Date(getValue()).toLocaleDateString()}</span> },
    { id: "items", header: "Items", accessorFn: (o) => (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0), cell: ({ getValue }) => <span className="text-sm">{getValue()} item(s)</span>, enableSorting: true },
    { accessorKey: "total", header: "Total", cell: ({ getValue }) => <span className="text-sm font-semibold">{format(getValue() || 0)}</span> },
    { id: "status", header: "Status", accessorFn: (o) => fState(o), cell: ({ getValue }) => statusBadge(getValue()) },
    { id: "actions", header: "", enableSorting: false, cell: ({ row }) => (
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`view-order-${row.original.order_id}`} onClick={() => setSelected(row.original)}><Eye className="w-4 h-4 mr-1" />Track</Button>
    ) },
  ], [format]);

  const table = useReactTable({
    data: orders, columns, state: { sorting }, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 8 } },
  });

  const canReturn = selected && ["delivered", "shipped"].includes(fState(selected));

  return (
    <Card className="p-0 overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id}>
                  {h.isPlaceholder ? null : h.column.getCanSort() ? (
                    <button className="inline-flex items-center gap-1 font-medium hover:text-foreground transition-colors" onClick={h.column.getToggleSortingHandler()} data-testid={`sort-${h.column.id}`}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      <ArrowUpDown className={`w-3.5 h-3.5 ${h.column.getIsSorted() ? "text-primary" : "opacity-40"}`} />
                    </button>
                  ) : flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 && (
            <TableRow><TableCell colSpan={columns.length} className="text-center py-14 text-muted-foreground">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />You haven't placed any orders yet.
            </TableCell></TableRow>
          )}
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} data-testid={`order-row-${row.original.order_id}`}>
              {row.getVisibleCells().map((cell) => <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <span className="text-xs text-muted-foreground">Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
          <div className="flex gap-2">
            <Button size="icon" variant="outline" className="h-8 w-8" data-testid="orders-prev-page" disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="icon" variant="outline" className="h-8 w-8" data-testid="orders-next-page" disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="order-track-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-mono">Order #{selected.order_id.slice(-8)}</DialogTitle></DialogHeader>
              <div className="space-y-5">
                <OrderProgressBar order={selected} />

                {(selected.carrier || selected.tracking_number) && (
                  <div className="flex flex-wrap items-center gap-2 text-sm rounded-xl bg-muted/60 px-3 py-2">
                    <Truck className="w-4 h-4 text-primary" />
                    {selected.carrier && <span className="font-medium">{selected.carrier}</span>}
                    {selected.tracking_number && <span className="font-mono text-muted-foreground">#{selected.tracking_number}</span>}
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Items</p>
                  {(selected.items || []).map((it, i) => (
                    <div key={`${it.product_id}-${i}`} className="flex items-center gap-3 text-sm">
                      <div className="w-11 h-11 rounded-lg bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                        {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <span className="flex-1 truncate">{it.title}</span>
                      <span className="text-muted-foreground">×{it.quantity}</span>
                      <span className="font-medium">{format((it.price || 0) * (it.quantity || 1))}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t font-bold"><span>Total</span><span className="font-display">{format(selected.total || 0)}</span></div>
                </div>

                {onMessageSeller && (() => {
                  const sellers = [...new Set((selected.items || []).map((it) => it.seller_id).filter(Boolean))];
                  if (sellers.length === 0) return null;
                  return (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Need help with this order?</p>
                      {sellers.map((sid) => {
                        const first = (selected.items || []).find((it) => it.seller_id === sid);
                        return (
                          <Button key={sid} variant="outline" className="w-full rounded-full justify-center" data-testid={`message-seller-${sid}`}
                            onClick={() => { onMessageSeller(sid, first?.product_id); setSelected(null); }}>
                            <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />Message seller
                          </Button>
                        );
                      })}
                    </div>
                  );
                })()}

                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">History</p>
                  <TrackingTimeline order={selected} />
                </div>

                {canReturn && (
                  <Button variant="outline" className="w-full rounded-full" data-testid={`request-return-${selected.order_id}`}
                    onClick={() => { onRequestReturn?.(selected); setSelected(null); }}>
                    <RotateCcw className="w-4 h-4 mr-2" />Request return / refund
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
